/**
 * CommentSection Component
 *
 * Displays route comments with threaded replies.
 * Allows users to add comments, reply to others, edit, and delete.
 */

import { useState, type CSSProperties, type FormEvent } from 'react';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import ReplyIcon from '@mui/icons-material/Reply';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { useComments, type CommentThread } from '@/hooks/useComments';
import { useAuth } from '@/hooks/useAuth';
import { ReportContentModal } from '@/components/moderation';
import type { RouteComment } from '@flash/shared';

/**
 * Props for CommentSection component
 */
interface CommentSectionProps {
  /** Route ID to show comments for */
  routeId: string;
  /** Gym ID for the route */
  gymId: string;
}

/**
 * Format relative time for display
 */
const formatRelativeTime = (timestamp: { seconds: number } | Date | undefined): string => {
  if (!timestamp) return '';

  const date =
    timestamp instanceof Date
      ? timestamp
      : new Date((timestamp as { seconds: number }).seconds * 1000);

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Single comment display component
 */
const CommentItem = ({
  comment,
  isReply,
  currentUserId,
  onReply,
  onEdit,
  onDelete,
  isSubmitting,
}: {
  comment: RouteComment;
  isReply: boolean;
  currentUserId: string | undefined;
  onReply?: () => void;
  onEdit: (text: string) => Promise<void>;
  onDelete: () => Promise<void>;
  isSubmitting: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const isOwner = currentUserId === comment.author.userId;
  const canReport = currentUserId && !isOwner;

  const handleSubmitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (editText.trim()) {
      await onEdit(editText.trim());
      setIsEditing(false);
    }
  };

  const handleDelete = async () => {
    await onDelete();
    setShowDeleteConfirm(false);
  };

  return (
    <div style={isReply ? replyItemStyle : commentItemStyle}>
      {/* Author info */}
      <div style={commentHeaderStyle}>
        <div style={authorInfoStyle}>
          {comment.author.photoURL ? (
            <img
              src={comment.author.photoURL}
              alt={comment.author.displayName}
              style={avatarStyle}
            />
          ) : (
            <div style={avatarPlaceholderStyle}>
              {comment.author.displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <span style={authorNameStyle}>{comment.author.displayName}</span>
          <span style={timestampStyle}>
            {formatRelativeTime(comment.createdAt as { seconds: number })}
            {comment.isEdited && ' (edited)'}
          </span>
        </div>
      </div>

      {/* Comment content */}
      {isEditing ? (
        <form onSubmit={handleSubmitEdit} style={editFormStyle}>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            style={editTextareaStyle}
            maxLength={500}
            autoFocus
          />
          <div style={editActionsStyle}>
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                setEditText(comment.text);
              }}
              style={cancelButtonStyle}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={saveButtonStyle}
              disabled={isSubmitting || !editText.trim()}
            >
              Save
            </button>
          </div>
        </form>
      ) : (
        <p style={commentTextStyle}>{comment.text}</p>
      )}

      {/* Actions */}
      {!isEditing && !showDeleteConfirm && (
        <div style={commentActionsStyle}>
          {onReply && !isReply && (
            <button onClick={onReply} style={actionButtonStyle} disabled={isSubmitting}>
              <ReplyIcon style={actionIconStyle} />
              Reply
            </button>
          )}
          {isOwner && (
            <>
              <button
                onClick={() => setIsEditing(true)}
                style={actionButtonStyle}
                disabled={isSubmitting}
              >
                <EditIcon style={actionIconStyle} />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                style={actionButtonStyle}
                disabled={isSubmitting}
              >
                <DeleteOutlineIcon style={actionIconStyle} />
                Delete
              </button>
            </>
          )}
          {canReport && (
            <button
              onClick={() => setShowReportModal(true)}
              style={actionButtonStyle}
              disabled={isSubmitting}
            >
              <FlagOutlinedIcon style={actionIconStyle} />
              Report
            </button>
          )}
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div style={deleteConfirmStyle}>
          <span style={deleteConfirmTextStyle}>Delete this comment?</span>
          <button
            onClick={() => setShowDeleteConfirm(false)}
            style={cancelButtonStyle}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button onClick={handleDelete} style={confirmDeleteButtonStyle} disabled={isSubmitting}>
            Delete
          </button>
        </div>
      )}

      {/* Report Modal */}
      <ReportContentModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        contentType="comment"
        contentId={comment.id}
        gymId={comment.gymId}
        contentPreview={comment.text}
      />
    </div>
  );
};

/**
 * Thread component (comment with replies)
 */
const CommentThreadItem = ({
  thread,
  currentUserId,
  onAddReply,
  onEditComment,
  onDeleteComment,
  isSubmitting,
}: {
  thread: CommentThread;
  currentUserId: string | undefined;
  onAddReply: (parentId: string, text: string) => Promise<void>;
  onEditComment: (commentId: string, text: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  isSubmitting: boolean;
}) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');

  const handleSubmitReply = async (e: FormEvent) => {
    e.preventDefault();
    if (replyText.trim()) {
      await onAddReply(thread.comment.id, replyText.trim());
      setReplyText('');
      setShowReplyInput(false);
    }
  };

  return (
    <div style={threadContainerStyle}>
      <CommentItem
        comment={thread.comment}
        isReply={false}
        currentUserId={currentUserId}
        onReply={() => setShowReplyInput(!showReplyInput)}
        onEdit={(text) => onEditComment(thread.comment.id, text)}
        onDelete={() => onDeleteComment(thread.comment.id)}
        isSubmitting={isSubmitting}
      />

      {/* Reply input */}
      {showReplyInput && (
        <form onSubmit={handleSubmitReply} style={replyInputContainerStyle}>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply..."
            style={replyTextareaStyle}
            maxLength={500}
            autoFocus
          />
          <div style={replyActionsStyle}>
            <button
              type="button"
              onClick={() => {
                setShowReplyInput(false);
                setReplyText('');
              }}
              style={cancelButtonStyle}
              disabled={isSubmitting}
            >
              <CloseIcon style={{ fontSize: '1rem' }} />
            </button>
            <button
              type="submit"
              style={sendButtonStyle}
              disabled={isSubmitting || !replyText.trim()}
            >
              <SendIcon style={{ fontSize: '1rem' }} />
            </button>
          </div>
        </form>
      )}

      {/* Replies */}
      {thread.replies.length > 0 && (
        <div style={repliesContainerStyle}>
          {thread.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              isReply={true}
              currentUserId={currentUserId}
              onEdit={(text) => onEditComment(reply.id, text)}
              onDelete={() => onDeleteComment(reply.id)}
              isSubmitting={isSubmitting}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * CommentSection - Main component for route comments
 */
export const CommentSection = ({ routeId, gymId }: CommentSectionProps) => {
  const { user } = useAuth();
  const {
    threads,
    loading,
    error,
    addComment,
    addReply,
    editComment,
    deleteComment,
    isSubmitting,
    totalCount,
  } = useComments(routeId, gymId);

  const [newCommentText, setNewCommentText] = useState('');
  const [showInput, setShowInput] = useState(false);

  const handleSubmitComment = async (e: FormEvent) => {
    e.preventDefault();
    if (newCommentText.trim()) {
      await addComment(newCommentText.trim());
      setNewCommentText('');
      setShowInput(false);
    }
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <ChatBubbleOutlineIcon style={headerIconStyle} />
          <span style={headerTitleStyle}>Comments</span>
        </div>
        <p style={loadingTextStyle}>Loading comments...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <ChatBubbleOutlineIcon style={headerIconStyle} />
          <span style={headerTitleStyle}>Comments</span>
        </div>
        <p style={errorTextStyle}>Failed to load comments</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <ChatBubbleOutlineIcon style={headerIconStyle} />
        <span style={headerTitleStyle}>Comments</span>
        {totalCount > 0 && <span style={countBadgeStyle}>{totalCount}</span>}
      </div>

      {/* Add comment button/form */}
      {user ? (
        showInput ? (
          <form onSubmit={handleSubmitComment} style={newCommentFormStyle}>
            <textarea
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              placeholder="Share your thoughts on this route..."
              style={newCommentTextareaStyle}
              maxLength={500}
              autoFocus
            />
            <div style={newCommentActionsStyle}>
              <span style={charCountStyle}>{newCommentText.length}/500</span>
              <button
                type="button"
                onClick={() => {
                  setShowInput(false);
                  setNewCommentText('');
                }}
                style={cancelButtonStyle}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={postButtonStyle}
                disabled={isSubmitting || !newCommentText.trim()}
              >
                Post
              </button>
            </div>
          </form>
        ) : (
          <button onClick={() => setShowInput(true)} style={addCommentButtonStyle}>
            Add a comment...
          </button>
        )
      ) : (
        <p style={signInPromptStyle}>Sign in to comment</p>
      )}

      {/* Comments list */}
      {threads.length > 0 ? (
        <div style={commentsListStyle}>
          {threads.map((thread) => (
            <CommentThreadItem
              key={thread.comment.id}
              thread={thread}
              currentUserId={user?.uid}
              onAddReply={addReply}
              onEditComment={editComment}
              onDeleteComment={deleteComment}
              isSubmitting={isSubmitting}
            />
          ))}
        </div>
      ) : (
        <p style={emptyStateStyle}>No comments yet. Be the first to share your thoughts!</p>
      )}
    </div>
  );
};

// Styles
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const headerIconStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const headerTitleStyle: CSSProperties = {
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: 'var(--color-text-primary, #1f2937)',
};

const countBadgeStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  borderRadius: '10px',
  padding: '2px 8px',
};

const loadingTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
  fontStyle: 'italic',
};

const errorTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: '#dc2626',
};

const addCommentButtonStyle: CSSProperties = {
  width: '100%',
  padding: '12px',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '8px',
  cursor: 'pointer',
  textAlign: 'left',
};

const signInPromptStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
  fontStyle: 'italic',
};

const newCommentFormStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const newCommentTextareaStyle: CSSProperties = {
  width: '100%',
  minHeight: '80px',
  padding: '12px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '8px',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const newCommentActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '8px',
};

const charCountStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-tertiary, #9ca3af)',
  marginRight: 'auto',
};

const cancelButtonStyle: CSSProperties = {
  padding: '6px 12px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const postButtonStyle: CSSProperties = {
  padding: '6px 16px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const commentsListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const emptyStateStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: 'var(--color-text-tertiary, #9ca3af)',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: '16px 0',
};

const threadContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const commentItemStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '12px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  borderRadius: '8px',
};

const replyItemStyle: CSSProperties = {
  ...commentItemStyle,
  marginLeft: '24px',
  backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
};

const commentHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const authorInfoStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const avatarStyle: CSSProperties = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  objectFit: 'cover',
};

const avatarPlaceholderStyle: CSSProperties = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  fontWeight: 600,
};

const authorNameStyle: CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const timestampStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-tertiary, #9ca3af)',
};

const commentTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: 'var(--color-text-primary, #1f2937)',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const commentActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  marginTop: '4px',
};

const actionButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary, #6b7280)',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};

const actionIconStyle: CSSProperties = {
  fontSize: '0.875rem',
};

const editFormStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const editTextareaStyle: CSSProperties = {
  width: '100%',
  minHeight: '60px',
  padding: '8px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '6px',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const editActionsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
};

const saveButtonStyle: CSSProperties = {
  padding: '6px 12px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const deleteConfirmStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '4px',
};

const deleteConfirmTextStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: '#dc2626',
  marginRight: 'auto',
};

const confirmDeleteButtonStyle: CSSProperties = {
  padding: '6px 12px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: '#dc2626',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const replyInputContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  marginLeft: '24px',
  alignItems: 'flex-start',
};

const replyTextareaStyle: CSSProperties = {
  flex: 1,
  minHeight: '40px',
  padding: '8px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '6px',
  resize: 'none',
  fontFamily: 'inherit',
};

const replyActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '4px',
};

const sendButtonStyle: CSSProperties = {
  padding: '8px',
  fontSize: '0.875rem',
  color: 'white',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const repliesContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

export default CommentSection;
